import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import DevicesIcon from '@mui/icons-material/Devices';

import LoadingButton from '../../components/buttons/Button';
import TopBar from '../../components/utils/TopBar';
import NA from '../../components/utils/NA';

import {
  getMyProfileInternalUser,
  setupInternalUserMfa,
  verifyInternalUserMfaSetup,
  disableInternalUserMfa,
  regenerateInternalUserMfaBackupCodes,
  getMyInternalUserSessions,
  revokeInternalUserSession,
  revokeAllOtherInternalUserSessions,
} from '../../api';

import style from "../../global.scss";


const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [enableModalOpen, setEnableModalOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);

  // ----------------------- fetch profile -----------------------
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await getMyProfileInternalUser();
      setProfile(data.data);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch profile");
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <Box width={"100%"}>
      <TopBar
        title={"Profile"}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        !profile
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <Typography>Profile information not available</Typography>
        </Box>
        :
        <Box
          className="flex__Column"
          gap={"20px"}
          maxWidth={"720px"}>

          {/* ---------- Basic information ---------- */}
          <Box
            className="block"
            p={"20px"}>
            <Typography variant='subtitle1' mb={"15px"}>
              Basic information
            </Typography>
            <Divider sx={{ borderColor: "#343434", mb: "15px" }}/>

            <Box className="flex__Column" gap={"12px"}>
              <ProfileRow
                label={"Name"}
                value={`${profile.first_name || ''} ${profile.last_name || ''}`.trim() || <NA />}
                />
              <ProfileRow
                label={"User name"}
                value={profile.user_name || <NA />}
                />
              <ProfileRow
                label={"Primary email"}
                value={profile.primary_email || <NA />}
                />
              <ProfileRow
                label={"Member since"}
                value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : <NA />}
                />
            </Box>
          </Box>

          {/* ---------- Active sessions ---------- */}
          <ActiveSessionsSection />

          {/* ---------- Multi-factor authentication ---------- */}
          <Box
            className="block"
            p={"20px"}>
            <Box className='flexCenterSBRow' mb={"15px"}>
              <Typography variant='subtitle1'>
                Multi-factor authentication
              </Typography>
              <Chip
                label={profile.mfa_enabled ? "Enabled" : "Disabled"}
                color={profile.mfa_enabled ? "success" : "warning"}
                size='small'
                />
            </Box>
            <Divider sx={{ borderColor: "#343434", mb: "15px" }}/>

            <Typography variant='body2' color={"grey"} mb={"20px"}>
              {
                profile.mfa_enabled
                ? "Your account is protected by an authenticator app. You will be asked for a 6-digit code each time you sign in."
                : "Add an extra layer of security to your account using an authenticator app such as Google Authenticator."
              }
            </Typography>

            <Box className='flexCenter_Row' gap={"10px"}>
              {
                profile.mfa_enabled
                ?
                <>
                  <LoadingButton
                    variant='outlined'
                    onClick={() => setRegenerateModalOpen(true)}>
                    Regenerate backup codes
                  </LoadingButton>
                  <LoadingButton
                    variant='contained'
                    color='error'
                    onClick={() => setDisableModalOpen(true)}>
                    Disable MFA
                  </LoadingButton>
                </>
                :
                <LoadingButton
                  variant='contained'
                  onClick={() => setEnableModalOpen(true)}>
                  Enable MFA
                </LoadingButton>
              }
            </Box>
          </Box>
        </Box>
      }

      <EnableMfaDialog
        open={enableModalOpen}
        onClose={() => setEnableModalOpen(false)}
        onSuccess={() => {
          setEnableModalOpen(false);
          fetchProfile();
        }}
        />
      <DisableMfaDialog
        open={disableModalOpen}
        onClose={() => setDisableModalOpen(false)}
        onSuccess={() => {
          setDisableModalOpen(false);
          fetchProfile();
        }}
        />
      <RegenerateBackupCodesDialog
        open={regenerateModalOpen}
        onClose={() => setRegenerateModalOpen(false)}
        />
    </Box>
  )
}

export default ProfilePage;


// ============================================================
//   Reusable label/value row
// ============================================================
const ProfileRow = ({ label, value }) => (
  <Box className='flexCenterSBRow'>
    <Typography variant='body2' color={"grey"}>{label}</Typography>
    <Typography variant='body2'>{value}</Typography>
  </Box>
);


// ============================================================
//   Active sessions section — lists all live sessions for this
//   internal user, marks the current one, and offers per-session
//   + bulk revoke controls (a.k.a. "log out from other devices").
// ============================================================
const ActiveSessionsSection = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data } = await getMyInternalUserSessions();
      setSessions(data.data || []);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch sessions");
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevoke = async (sessionId) => {
    setBusyId(sessionId);
    try {
      await revokeInternalUserSession(sessionId);
      toast.success("Session revoked");
      fetchSessions();
    } catch (err) {
      toast.error(err?.message || "Failed to revoke session");
    }
    setBusyId(null);
  }

  const handleRevokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      await revokeAllOtherInternalUserSessions();
      toast.success("Other sessions revoked");
      fetchSessions();
    } catch (err) {
      toast.error(err?.message || "Failed to revoke other sessions");
    }
    setRevokingAll(false);
  }

  const hasOtherSessions = sessions.some((s) => !s.is_current);

  return (
    <Box className="block" p={"20px"}>
      <Box className='flexCenterSBRow' mb={"15px"}>
        <Typography variant='subtitle1'>
          Active sessions
        </Typography>
        <Chip
          label={sessions.length}
          color='info'
          size='small'
          />
      </Box>
      <Divider sx={{ borderColor: "#343434", mb: "15px" }}/>

      <Typography variant='body2' color={"grey"} mb={"15px"}>
        These are the devices currently signed in to your account. Revoke anything you don't recognise.
      </Typography>

      {
        loading
        ?
        <Box height={"120px"} className="flexCenterCenterRow">
          <CircularProgress size={28}/>
        </Box>
        :
        sessions.length === 0
        ?
        <Typography variant='body2' color={"grey"}>
          No active sessions.
        </Typography>
        :
        <Box className="flex__Column" gap={"10px"}>
          {
            sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                busy={busyId === s.id}
                onRevoke={() => handleRevoke(s.id)}
                />
            ))
          }
          {
            hasOtherSessions &&
            <Box className='flexCenterFERow' mt={"10px"}>
              <LoadingButton
                variant='outlined'
                startIcon={<LogoutIcon/>}
                loading={revokingAll}
                onClick={handleRevokeAllOthers}>
                Log out all other devices
              </LoadingButton>
            </Box>
          }
        </Box>
      }
    </Box>
  )
}


// ============================================================
//   Single session row inside the Active sessions section
// ============================================================
const SessionRow = ({ session, busy, onRevoke }) => {
  const parsed = parseUserAgent(session.user_agent);
  const lastSeen = session.last_seen_at
    ? formatRelativeTime(new Date(session.last_seen_at))
    : null;

  return (
    <Box
      className='flexCenterSBRow'
      sx={{
        background: "rgba(0, 0, 0, 0.32)",
        padding: "12px 15px",
        borderRadius: "12px",
        gap: "15px",
      }}>
      <Box className='flexCenter_Row' gap={"12px"} sx={{ flex: 1, minWidth: 0 }}>
        <DevicesIcon sx={{ color: "grey" }}/>
        <Box className='flex__Column' sx={{ minWidth: 0 }}>
          <Box className='flexCenter_Row' gap={"8px"}>
            <Typography variant='body2'>
              {parsed}
            </Typography>
            {
              session.is_current &&
              <Chip
                label="This device"
                color='success'
                size='small'
                />
            }
          </Box>
          <Typography variant='caption' color={"grey"} className='longText'>
            {session.ip || <NA />} {lastSeen && <>· last active {lastSeen}</>}
          </Typography>
        </Box>
      </Box>
      {
        !session.is_current &&
        <LoadingButton
          variant='outlined'
          color='error'
          loading={busy}
          onClick={onRevoke}>
          Revoke
        </LoadingButton>
      }
    </Box>
  )
}


// ============================================================
//   Lightweight helpers — no external deps
// ============================================================
const parseUserAgent = (ua) => {
  if (!ua) return "Unknown device";
  const uaLower = ua.toLowerCase();
  let browser = "Browser";
  if (uaLower.includes("edg/")) browser = "Edge";
  else if (uaLower.includes("chrome") && !uaLower.includes("chromium")) browser = "Chrome";
  else if (uaLower.includes("firefox")) browser = "Firefox";
  else if (uaLower.includes("safari") && !uaLower.includes("chrome")) browser = "Safari";
  else if (uaLower.includes("opr/") || uaLower.includes("opera")) browser = "Opera";

  let os = "Unknown OS";
  if (uaLower.includes("windows")) os = "Windows";
  else if (uaLower.includes("mac os") || uaLower.includes("macintosh")) os = "macOS";
  else if (uaLower.includes("iphone") || uaLower.includes("ios")) os = "iOS";
  else if (uaLower.includes("android")) os = "Android";
  else if (uaLower.includes("linux")) os = "Linux";

  return `${browser} on ${os}`;
}

const formatRelativeTime = (date) => {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}


// ============================================================
//   Enable MFA (setup) dialog
//     Step 1: backend returns QR + setup_id, user scans
//     Step 2: user enters 6-digit code, backend verifies + returns backup codes
//     Step 3: user sees + copies backup codes
// ============================================================
const EnableMfaDialog = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [setupId, setSetupId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [otp, setOtp] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setOtp("");
      setBackupCodes([]);
      initiateSetup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const initiateSetup = async () => {
    setLoading(true);
    try {
      const { data } = await setupInternalUserMfa();
      setSetupId(data.data.setup_id);
      setQrCode(data.data.qr_code_data_url);
      setManualKey(data.data.manual_entry_key);
    } catch (err) {
      toast.error(err?.message || "Failed to start MFA setup");
      onClose();
    }
    setLoading(false);
  }

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code from your authenticator");
      return;
    }
    setLoading(true);
    try {
      const { data } = await verifyInternalUserMfaSetup({
        setup_id: setupId,
        otp: otp,
      });
      setBackupCodes(data.data.backup_codes || []);
      setStep(2);
      toast.success("MFA enabled successfully");
    } catch (err) {
      toast.error(err?.message || "Invalid code, please try again");
    }
    setLoading(false);
  }

  const handleClose = () => {
    if (step === 2) {
      onSuccess();
    } else {
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="enable-mfa-modal-title">
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box minWidth={"450px"} maxWidth={"500px"} className="modal">

          <Box sx={{ margin: "15px" }}>
            <Typography variant='h6' sx={{ color: style["primary"] }}>
              {step === 1 ? "Enable MFA" : "Save your backup codes"}
            </Typography>
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ margin: "15px" }}>
            {
              loading && step === 1 && !qrCode
              ?
              <Box height={"260px"} className="flexCenterCenterRow">
                <CircularProgress />
              </Box>
              :
              step === 1
              ?
              <Box className="flex__Column" gap={"15px"}>
                <Typography variant='body2'>
                  Scan the QR code below using Google Authenticator (or any TOTP app), then enter the 6-digit code it shows.
                </Typography>
                {
                  qrCode &&
                  <Box className="flexCenterCenterRow">
                    <img
                      src={qrCode}
                      alt="MFA QR code"
                      style={{ width: "200px", height: "200px", background: "white", padding: "10px", borderRadius: "12px" }}
                      />
                  </Box>
                }
                {
                  manualKey &&
                  <Box
                    className='flexCenterSBRow'
                    sx={{ background: "rgba(0, 0, 0, 0.32)", padding: "8px 12px", borderRadius: "8px" }}>
                    <Typography variant='caption'>
                      Or enter key manually:&nbsp;
                      <span style={{ fontFamily: "monospace" }}>{manualKey}</span>
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={() => {
                        navigator.clipboard.writeText(manualKey);
                        toast.success("Key copied");
                      }}>
                      <ContentCopyIcon fontSize='small' sx={{ color: style["primary_light"] }}/>
                    </IconButton>
                  </Box>
                }
                <TextField
                  variant='outlined'
                  label='6-digit code'
                  placeholder='000000'
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  />
              </Box>
              :
              <Box className="flex__Column" gap={"12px"}>
                <Typography variant='body2'>
                  Store these one-time backup codes somewhere safe. Each code can be used once if you lose access to your authenticator. They will NOT be shown again.
                </Typography>
                <Box
                  sx={{
                    background: "rgba(0, 0, 0, 0.32)",
                    padding: "15px",
                    borderRadius: "12px",
                    fontFamily: "monospace",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}>
                  {
                    backupCodes.map((code, i) => (
                      <Typography
                        key={i}
                        variant='body2'
                        sx={{ fontFamily: "monospace" }}>
                        {code}
                      </Typography>
                    ))
                  }
                </Box>
                <LoadingButton
                  variant='outlined'
                  onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join("\n"));
                    toast.success("Backup codes copied");
                  }}>
                  Copy all
                </LoadingButton>
              </Box>
            }
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            {
              step === 1
              ?
              <>
                <LoadingButton variant='outlined' onClick={onClose}>Cancel</LoadingButton>
                <LoadingButton
                  variant='contained'
                  loading={loading}
                  onClick={handleVerify}>
                  Verify & enable
                </LoadingButton>
              </>
              :
              <LoadingButton variant='contained' onClick={handleClose}>
                Done
              </LoadingButton>
            }
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}


// ============================================================
//   Disable MFA dialog — requires current TOTP code
// ============================================================
const DisableMfaDialog = ({ open, onClose, onSuccess }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setOtp("");
  }, [open]);

  const handleDisable = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code from your authenticator");
      return;
    }
    setLoading(true);
    try {
      await disableInternalUserMfa({ otp });
      toast.success("MFA disabled");
      onSuccess();
    } catch (err) {
      toast.error(err?.message || "Invalid code, please try again");
    }
    setLoading(false);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="disable-mfa-modal-title">
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box minWidth={"400px"} maxWidth={"460px"} className="modal">

          <Box sx={{ margin: "15px" }}>
            <Typography variant='h6' sx={{ color: style["primary"] }}>
              Disable MFA
            </Typography>
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ margin: "15px" }}>
            <Box className="flex__Column" gap={"12px"}>
              <Typography variant='body2'>
                Enter the 6-digit code from your authenticator app to confirm you want to disable MFA.
              </Typography>
              <TextField
                variant='outlined'
                label='6-digit code'
                placeholder='000000'
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />
            </Box>
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <LoadingButton variant='outlined' onClick={onClose}>Cancel</LoadingButton>
            <LoadingButton
              variant='contained'
              color='error'
              loading={loading}
              onClick={handleDisable}>
              Disable MFA
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}


// ============================================================
//   Regenerate backup codes dialog — requires current TOTP code
// ============================================================
const RegenerateBackupCodesDialog = ({ open, onClose }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [newCodes, setNewCodes] = useState(null);

  useEffect(() => {
    if (open) {
      setOtp("");
      setNewCodes(null);
    }
  }, [open]);

  const handleRegenerate = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code from your authenticator");
      return;
    }
    setLoading(true);
    try {
      const { data } = await regenerateInternalUserMfaBackupCodes({ otp });
      setNewCodes(data.data.backup_codes || []);
      toast.success("New backup codes generated. Old codes are no longer valid.");
    } catch (err) {
      toast.error(err?.message || "Invalid code, please try again");
    }
    setLoading(false);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="regenerate-mfa-modal-title">
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box minWidth={"450px"} maxWidth={"500px"} className="modal">

          <Box sx={{ margin: "15px" }}>
            <Typography variant='h6' sx={{ color: style["primary"] }}>
              {newCodes ? "Save your new backup codes" : "Regenerate backup codes"}
            </Typography>
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ margin: "15px" }}>
            {
              !newCodes
              ?
              <Box className="flex__Column" gap={"12px"}>
                <Typography variant='body2'>
                  Regenerating will invalidate your existing backup codes. Enter a fresh 6-digit code from your authenticator to continue.
                </Typography>
                <TextField
                  variant='outlined'
                  label='6-digit code'
                  placeholder='000000'
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  />
              </Box>
              :
              <Box className="flex__Column" gap={"12px"}>
                <Typography variant='body2'>
                  Store these codes safely. They will NOT be shown again.
                </Typography>
                <Box
                  sx={{
                    background: "rgba(0, 0, 0, 0.32)",
                    padding: "15px",
                    borderRadius: "12px",
                    fontFamily: "monospace",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}>
                  {
                    newCodes.map((code, i) => (
                      <Typography
                        key={i}
                        variant='body2'
                        sx={{ fontFamily: "monospace" }}>
                        {code}
                      </Typography>
                    ))
                  }
                </Box>
                <LoadingButton
                  variant='outlined'
                  startIcon={<RefreshIcon/>}
                  onClick={() => {
                    navigator.clipboard.writeText(newCodes.join("\n"));
                    toast.success("Backup codes copied");
                  }}>
                  Copy all
                </LoadingButton>
              </Box>
            }
          </Box>
          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            {
              !newCodes
              ?
              <>
                <LoadingButton variant='outlined' onClick={onClose}>Cancel</LoadingButton>
                <LoadingButton
                  variant='contained'
                  loading={loading}
                  onClick={handleRegenerate}>
                  Generate new codes
                </LoadingButton>
              </>
              :
              <LoadingButton variant='contained' onClick={onClose}>
                Done
              </LoadingButton>
            }
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
